/**
 * aggregate-columns.ts
 * ====================
 * Columnar port of aggregateCandles from replay-utils.ts.
 * Runs in the Web Worker at ingest time to build pre-aggregated tiers,
 * eliminating per-TF-switch main-thread aggregation.
 */

import { ColumnarBuilder, type CandleColumns } from "@/lib/lytick/columnar";
import { getBucketStart } from "@/lib/lytick/replay-utils";

/** Timeframe tiers (in minutes) pre-computed at ingest. */
export const TIER_TF_MINUTES = [5, 15, 30, 60, 240, 1440, 10080] as const;

/**
 * Aggregate a CandleColumns into a coarser timeframe.
 * Uses the same calendar-aligned UTC bucketing as aggregateCandles.
 *
 * @param base      Source columns (any granularity)
 * @param tfMinutes Target timeframe in minutes (must be > base granularity)
 */
export function aggregateColumns(
  base: CandleColumns,
  tfMinutes: number,
): CandleColumns {
  if (base.length === 0 || tfMinutes <= 0) {
    return { time: new Float64Array(0), open: new Float64Array(0), high: new Float64Array(0), low: new Float64Array(0), close: new Float64Array(0), volume: new Float64Array(0), length: 0 };
  }

  const estimatedBuckets = Math.ceil(base.length / tfMinutes) + 2;
  const builder = new ColumnarBuilder(estimatedBuckets);

  let currentBucket = -1;
  let bOpen = 0;
  let bHigh = 0;
  let bLow = 0;
  let bClose = 0;
  let bVol = 0;

  for (let i = 0; i < base.length; i++) {
    const bucket = getBucketStart(base.time[i], tfMinutes);

    if (bucket !== currentBucket) {
      if (currentBucket !== -1) {
        builder.append(currentBucket, bOpen, bHigh, bLow, bClose, bVol);
      }
      currentBucket = bucket;
      bOpen = base.open[i];
      bHigh = base.high[i];
      bLow = base.low[i];
      bClose = base.close[i];
      bVol = base.volume[i];
    } else {
      if (base.high[i] > bHigh) bHigh = base.high[i];
      if (base.low[i] < bLow) bLow = base.low[i];
      bClose = base.close[i];
      bVol += base.volume[i];
    }
  }

  if (currentBucket !== -1) {
    builder.append(currentBucket, bOpen, bHigh, bLow, bClose, bVol);
  }

  return builder.finalize();
}

/**
 * Aggregate a partial range of base columns (for the in-progress bucket
 * at the replay cursor). Returns null if range is empty.
 */
export function aggregatePartialBucket(
  base: CandleColumns,
  fromIdx: number,
  toIdx: number, // inclusive
): { open: number; high: number; low: number; close: number; volume: number } | null {
  const end = Math.min(toIdx, base.length - 1);
  if (fromIdx > end || fromIdx >= base.length) return null;

  let h = base.high[fromIdx];
  let l = base.low[fromIdx];
  let v = base.volume[fromIdx];

  for (let i = fromIdx + 1; i <= end; i++) {
    if (base.high[i] > h) h = base.high[i];
    if (base.low[i] < l) l = base.low[i];
    v += base.volume[i];
  }

  return {
    open: base.open[fromIdx],
    high: h,
    low: l,
    close: base.close[end],
    volume: v,
  };
}

/**
 * Build all standard tier columns from a base CandleColumns.
 * Skips tiers ≤ base granularity.
 */
export function buildAllTiers(
  base: CandleColumns,
  baseGranularitySeconds: number,
): Map<number, CandleColumns> {
  const baseMinutes = Math.max(1, baseGranularitySeconds / 60);
  const tiers = new Map<number, CandleColumns>();

  for (const tfMinutes of TIER_TF_MINUTES) {
    if (tfMinutes > baseMinutes) {
      tiers.set(tfMinutes, aggregateColumns(base, tfMinutes));
    }
  }

  return tiers;
}
